<script>
import { mapGetters, mapMutations } from "vuex";
import { textoFiltro } from "@/helpers/apuracaoCapex";
import AsyncExportButton from "@/components/async-export-button.vue";
import GTooltip from "@/components/g-tooltip.vue";
import { enquadramento as api } from "@/api/apuracaocapex";
import { ITEMS_PER_PAGE_OPTIONS_SERVE_SIDE } from "@/helpers/itemsPerPageOptions";

/** Espera (ms) antes de buscar, depois que o usuário para de digitar. */
const ESPERA_DIGITACAO = 800;

export default {
  name: "apuracao-capex-enquadramento",
  components: { AsyncExportButton, GTooltip },
  computed: {
    ...mapGetters(["clientId", "anoBase", "getServerSidePaginationItemsPerPage"]),

    /** Checkbox do cabeçalho marcado — considera o total do endpoint, não só a página. */
    todosSelecionados() {
      return (
        this.totalRegistros > 0 && this.selecionadosMap.size === this.totalRegistros
      );
    },
    /** Checkbox do cabeçalho em estado **indeterminado** (seleção parcial). */
    algunsSelecionados() {
      return (
        this.selecionadosMap.size > 0 && this.selecionadosMap.size < this.totalRegistros
      );
    },
    /** A página atual está toda selecionada? (base do clique no cabeçalho) */
    paginaTodaSelecionada() {
      return (
        (this.rows || []).length > 0 &&
        this.rows.every((linha) => this.selecionadosMap.has(linha.id))
      );
    },

    /** Ano-base efetivo (o store pode ainda não ter refletido a troca do filtro). */
    anoEfetivo() {
      return this.anoBase ? String(this.anoBase) : String(new Date().getFullYear());
    },

    beneficios() {
      return api.BENEFICIOS;
    },

    situacoes() {
      return api.SITUACOES;
    },

    itemsPerPageOptions() {
      return ITEMS_PER_PAGE_OPTIONS_SERVE_SIDE;
    },

    /** Objeto de paginação no formato do `v-data-footer`. */
    paginacao() {
      const porPagina = this.porPagina > 0 ? this.porPagina : this.totalRegistros;
      const totalPaginas = Math.max(1, Math.ceil(this.totalRegistros / (porPagina || 1)));
      const paginaAtual = Math.min(this.pagina, totalPaginas);
      const inicio = (paginaAtual - 1) * porPagina;

      return {
        page: paginaAtual,
        itemsPerPage: this.porPagina,
        pageStart: this.rows.length ? inicio : 0,
        pageStop: this.rows.length ? Math.min(this.totalRegistros, inicio + porPagina) : 0,
        pageCount: totalPaginas,
        itemsLength: this.totalRegistros,
      };
    },

    /** Anos do filtro de ano-base (o atual e os cinco anteriores). */
    anosOptions() {
      const atual = Number(new Date().getFullYear());
      const anos = [];

      for (let ano = atual; ano > atual - 6; ano--) {
        anos.push(String(ano));
      }

      return anos;
    },

    /** Cabeçalhos da tabela — os `value` são os campos de ordenação da API. */
    headers() {
      return [
        { text: "Projeto", value: "nome" },
        { text: "Benefício", value: "trabalho", width: "110px" },
        { text: "Enquadramento no benefício", value: "enquadramento", width: "160px", nowrapHeader: false },
        { text: "Tipo de pesquisa", value: "tipo_pesquisa", width: "130px", nowrapHeader: false },
        { text: "Horas em timesheet", value: "horas_timesheet", align: "end", width: "120px", nowrapHeader: false },
        { text: "Dispêndio apurado", value: "dispendio", align: "end", width: "155px", nowrapHeader: false },
        { text: `Ativo intangível em ${this.filtroAno}`, value: "ativo_intangivel", width: "160px", nowrapHeader: false },
        { text: "", value: "acoes", width: "120px", sortable: false },
      ];
    },
  },
  data() {
    return {
      loading: false,
      sombraAcoes: false,
      rows: [],
      totalRegistros: 0,
      totais: { sim: 0, nao: 0, nao_avaliado: 0 },
      opcoes: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
      selecionados: [],
      /** Seleção global (todas as páginas) por id — padrão do `table-v`/`MasterDetail`. */
      selecionadosMap: new Map(),
      filtroAno: String(new Date().getFullYear()),
      filtroNome: "",
      filtroBeneficio: "",
      filtroSituacao: "",
      ordenacao: {},
      pagina: 1,
      porPagina: 10,
      ultimaAssinatura: "",
      salvando: false,
      timerBusca: null,
      dialogObservacao: false,
      projetoEmEdicao: null,
      observacao: "",
    };
  },
  created() {
    this.filtroAno = this.anoEfetivo;
    this.porPagina = Number(this.getServerSidePaginationItemsPerPage) || 10;
    this.opcoes = { page: 1, itemsPerPage: this.porPagina, sortBy: [], sortDesc: [] };
  },
  watch: {
    clientId() {
      this.carregar({ page: 1 });
    },
    filtroNome() {
      this.agendarBusca();
    },
    filtroBeneficio() {
      this.buscarSeMudou();
    },
    filtroSituacao() {
      this.buscarSeMudou();
    },
  },
  mounted() {
    this.observarSombraAcoes();
  },
  beforeDestroy() {
    this.pararSombraAcoes();
  },
  methods: {
    /** Elemento que rola a tabela (wrapper do `v-data-table`). */
    wrapperTabela() {
      return this.$el.querySelector(".v-data-table__wrapper");
    },

    /** Coluna fixa: sombra só enquanto houver conteúdo escondido à direita. */
    atualizarSombraAcoes() {
      const wrapper = this.wrapperTabela();

      if (!wrapper) return;

      const escondido = wrapper.scrollWidth - wrapper.clientWidth;

      this.sombraAcoes = escondido > 1
        && wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth - 1;
    },

    /** Observa rolagem/tamanho da tabela (o ResizeObserver cobre o menu lateral). */
    observarSombraAcoes() {
      const wrapper = this.wrapperTabela();

      if (!wrapper) return;

      this.aoRolarTabela = () => this.atualizarSombraAcoes();
      wrapper.addEventListener("scroll", this.aoRolarTabela, { passive: true });
      window.addEventListener("resize", this.aoRolarTabela);

      if (typeof ResizeObserver !== "undefined") {
        this.observadorTabela = new ResizeObserver(() => this.atualizarSombraAcoes());
        this.observadorTabela.observe(wrapper);
      }

      this.$nextTick(() => this.atualizarSombraAcoes());
    },

    /** Desliga os observadores ao sair da tela. */
    pararSombraAcoes() {
      const wrapper = this.wrapperTabela();

      if (wrapper && this.aoRolarTabela) wrapper.removeEventListener("scroll", this.aoRolarTabela);
      if (this.aoRolarTabela) window.removeEventListener("resize", this.aoRolarTabela);
      if (this.observadorTabela) this.observadorTabela.disconnect();
    },
    /** Busca a página na API (o overlay global acompanha a chamada). */
    carregar(params) {
      const opcoes = params || {};

      this.ultimaAssinatura = this.assinaturaFiltros();
      // A grade vai ser trocada: a tooltip aberta ficaria presa na célula antiga.
      this.fecharTooltip();
      this.loading = true;

      api
        .listarEnquadramentos({
          empresaId: this.clientId,
          anoBase: this.filtroAno || this.anoEfetivo,
          page: opcoes.page || this.pagina,
          perPage: opcoes.perPage || this.porPagina,
          sort: opcoes.sort || this.ordenacao,
          filtros: {
            nome: textoFiltro(this.filtroNome),
            trabalho: this.filtroBeneficio,
            ativoIntangivel: this.filtroSituacao,
          },
        })
        .then((resultado) => {
          this.loading = false;
          this.rows = resultado.rows || [];
          // A seleção persiste entre páginas: reflete na grade o que já estava
          // marcado no mapa (os itens das outras páginas continuam selecionados).
          this.selecionados = this.rows.filter((linha) =>
            this.selecionadosMap.has(linha.id)
          );
          this.totalRegistros = resultado.total || 0;
          this.totais = resultado.totais || { sim: 0, nao: 0, nao_avaliado: 0 };
          this.pagina = resultado.page || 1;
          this.porPagina = resultado.perPage || this.porPagina;
          this.$nextTick(() => this.atualizarSombraAcoes());
        })
        .catch((erro) => {
          this.loading = false;
          this.rows = [];
          this.totalRegistros = 0;
          this.notify(erro);

          if (!erro.response) console.error(erro);
        });
    },

    /** Assinatura dos filtros efetivos (texto com `trim`) — evita chamada repetida. */
    assinaturaFiltros() {
      return [
        this.filtroAno,
        (this.filtroPeriodo || []).join(".."),
        textoFiltro(this.filtroNome),
        this.filtroBeneficio,
        this.filtroSituacao,
      ].join("|");
    },

    /** Busca só quando o filtro efetivo mudou (evita chamadas iguais em sequência). */
    buscarSeMudou() {
      if (this.assinaturaFiltros() === this.ultimaAssinatura) return;

      this.carregar({ page: 1 });
    },

    /** Agenda a busca por texto: só dispara quando o usuário para de digitar. */
    agendarBusca() {
      clearTimeout(this.timerBusca);

      this.timerBusca = setTimeout(() => {
        this.timerBusca = null;
        this.buscarSeMudou();
      }, ESPERA_DIGITACAO);
    },

    onChangePage(options) {
      const sortBy = options.sortBy || [];
      const sortDesc = options.sortDesc || [];
      const sort = sortBy.reduce(
        (acumulado, campo, indice) => ({ ...acumulado, [campo]: sortDesc[indice] ? "desc" : "asc" }),
        {}
      );

      this.ordenacao = sort;
      this.carregar({ page: options.page, perPage: options.itemsPerPage, sort });
    },

    ...mapMutations(["setAnoBase", "setPaginationItemsPerPage"]),

    /** Troca do ano-base: o valor é **global** (Vuex) e acompanha o usuário entre as telas. */
    trocarAno() {
      this.setAnoBase(this.filtroAno);
      this.carregar({ page: 1 });
    },

    onFooterOptions(options) {
      this.opcoes = { ...options, itemsPerPage: Number(options.itemsPerPage) };

      this.setPaginationItemsPerPage(this.opcoes.itemsPerPage);
    },

    /** Alinhamento do conteúdo do cabeçalho (o ícone fica sempre à direita do texto). */
    estiloCabecalho(head) {
      let alinhamento = "flex-start";

      if (head.align === "end") alinhamento = "flex-end";
      else if (head.align === "center") alinhamento = "center";

      return { justifyContent: alinhamento };
    },

    /** A coluna está ordenada? (usado pelo cabeçalho customizado) */
    colunaOrdenada(head, options) {
      return (options.sortBy || []).indexOf(head.value) >= 0;
    },

    /** A ordenação da coluna é descendente? */
    colunaDescendente(head, options) {
      const indice = (options.sortBy || []).indexOf(head.value);
      return indice >= 0 && !!(options.sortDesc || [])[indice];
    },


    /** O projeto tem observação? */
    temObservacao(item) {
      return !!item.observacao;
    },

    /** Nome completo do benefício (usado nos tooltips das pílulas LB/LI). */
    nomeBeneficio(trabalho) {
      return api.labelBeneficio(trabalho);
    },

    /** O texto do elemento passa de 2 linhas (ou seja, está cortado)? */
    estaTruncado(elemento) {
      const estilo = window.getComputedStyle(elemento);
      const alturaLinha = parseFloat(estilo.lineHeight) || parseFloat(estilo.fontSize) * 1.2;
      const limite = Math.max(Math.ceil(alturaLinha * 2), elemento.clientHeight);

      return elemento.scrollHeight > limite + 1;
    },

    /** Abre a tooltip só quando o texto do elemento está realmente cortado. */
    mostrarTooltip(evento, texto) {
      const elemento = evento.currentTarget;
      if (!this.estaTruncado(elemento)) return;

      this.$refs.tooltip.show(elemento, texto);
    },

    fecharTooltip() {
      if (this.$refs.tooltip) this.$refs.tooltip.hide();
    },

    corSituacao(valor) {
      if (valor === "Sim") return "green lighten-4";
      if (valor === "Não") return "red lighten-4";
      return "grey lighten-3";
    },

    classeSituacao(valor) {
      if (valor === "Sim") return "green--text text--darken-3";
      if (valor === "Não") return "red--text text--darken-3";
      return "grey--text text--darken-2";
    },

    corBeneficio(trabalho) {
      return Number(trabalho) === 2 ? "deep-purple lighten-4" : "blue lighten-4";
    },

    classeBeneficio(trabalho) {
      return Number(trabalho) === 2 ? "deep-purple--text text--darken-3" : "blue--text text--darken-3";
    },

    /** Idempotente: clicar de novo no mesmo botão não altera nada. */
    marcar(item, valor) {
      if (item.ativo_intangivel === valor) return;

      this.gravar({
        projetos: [item.id],
        ativo_intangivel: valor === "Sim",
      });
    },

    /** Limpa a seleção (todas as páginas) — ação explícita do usuário. */
    limparSelecao() {
      this.selecionadosMap = new Map();
      this.selecionados = [];
    },
    /**
     * Sincroniza a página atual com o mapa global (as outras páginas seguem marcadas).
     * @param {Array<Object>} itens linhas marcadas na página atual
     */
    aoSelecionar(itens) {
      const marcados = itens || [];
      const idsDaPagina = new Set((this.rows || []).map((linha) => linha.id));

      idsDaPagina.forEach((id) => this.selecionadosMap.delete(id));
      marcados.forEach((item) => this.selecionadosMap.set(item.id, item));

      // `Map` não é reativo por chave: trocar a instância atualiza os contadores.
      this.selecionadosMap = new Map(this.selecionadosMap);
      this.selecionados = marcados;
    },
    marcarLote(valor) {
      const ids = Array.from(this.selecionadosMap.values()).map((linha) => linha.id);

      if (!ids.length) return;

      // Os itens vão mudar de situação: a seleção não se aplica mais.
      this.selecionadosMap = new Map();
      this.selecionados = [];

      this.gravar({ projetos: ids, ativo_intangivel: valor === "Sim" });
    },

    abrirObservacao(item) {
      this.projetoEmEdicao = item;
      this.observacao = item.observacao || "";
      this.dialogObservacao = true;
    },

    /** Salva a observação em edição (`null` apaga a observação). */
    salvarObservacao() {
      if (!this.projetoEmEdicao) return;

      const item = this.projetoEmEdicao;

      this.dialogObservacao = false;
      this.projetoEmEdicao = null;

      this.gravar({ projetos: [item.id], observacao: this.observacao || null });
    },

    /** Grava marcação/observação na API e recarrega a lista e os contadores. */
    gravar(corpo) {
      if (this.salvando) return Promise.resolve();

      this.salvando = true;

      const payload = {
        empresaId: this.clientId,
        anoBase: this.filtroAno || this.anoEfetivo,
        projetos: corpo.projetos,
      };

      if (corpo.ativo_intangivel !== undefined) payload.ativoIntangivel = corpo.ativo_intangivel;
      if (corpo.observacao !== undefined) payload.observacao = corpo.observacao;

      return api
        .salvarEnquadramento(payload)
        .then(() => this.carregar({ page: this.pagina }))
        .catch((erro) => {
          this.notify(erro);

          if (!erro.response) console.error(erro);

          this.carregar({ page: this.pagina });
        })
        .finally(() => {
          this.salvando = false;
        });
    },

    /** Tipo de pesquisa exibido (a API manda o rótulo; vazio ⇒ "Indefinido"). */
    textoPesquisa(item) {
      return api.labelTipoPesquisa(item.tipo_pesquisa);
    },
  },
};
</script>

<template>
  <div class="content">
    <!-- Cabeçalho da tela -->
    <div class="header px-3 pt-3">
      <h3 class="h3 bold mb-0">Enquadramento de Projetos como Ativo Intangível</h3>
    </div>

    <!-- Faixa: ano-base ou seleção em lote -->
    <div
      class="faixa-selecao mt-3 mb-3 px-3 py-2 d-flex align-center"
      :style="{ backgroundColor: backColor }"
    >
      <span class="white--text font-weight-medium caption">
        <template v-if="!selecionadosMap.size">
          Projetos de Lei do Bem / Lei de Informática do ano-base que são ativo intangível
        </template>
        <template v-else>
          {{ selecionadosMap.size }} projeto{{ selecionadosMap.size > 1 ? "s" : "" }}
          selecionado{{ selecionadosMap.size > 1 ? "s" : "" }}
        </template>
      </span>

      <div v-if="selecionadosMap.size" class="d-flex align-center ml-3">
        <v-btn color="white" small class="primary--text font-weight-bold mr-2" @click="marcarLote('Sim')">
          Sim
        </v-btn>
        <v-btn color="white" small class="primary--text font-weight-bold" @click="marcarLote('Não')">
          Não
        </v-btn>
        <v-btn text small class="white--text ml-2" @click="limparSelecao">
          Limpar seleção
        </v-btn>
      </div>
    </div>

    <v-card :color="tableColor" elevation="0">
      <v-card-text class="barra-acoes d-flex flex-wrap align-center">
        <!-- Atualizar -->
        <div
          class="table-v-action-button mr-2 alinhar-campo actionBarButtons"
          @click="carregar({ page: 1, recarregar: true })"
        >
          <v-icon>mdi-refresh</v-icon>
          Atualizar
        </div>

        <AsyncExportButton
          class="mr-2 alinhar-campo"
          aria-label="exportacaoAssincrona"
          type="capex-enquadramento"
          :ano-base="filtroAno"
        >
          <v-icon>mdi-download-outline</v-icon> Exportar
        </AsyncExportButton>

        <!-- Filtros (label acima do campo) -->
        <div class="filtro mr-2">
          <label class="filtro-label" for="filtro-ano">Ano-base</label>
          <v-select
            id="filtro-ano"
            v-model="filtroAno"
            :items="anosOptions"
            class="table-v-action-button"
            dense
            hide-details
            outlined
            style="max-width: 120px"
            @change="trocarAno"
          ></v-select>
        </div>

        <div class="filtro mr-2">
          <label class="filtro-label" for="filtro-nome">Buscar projeto</label>
          <v-text-field
            id="filtro-nome"
            v-model="filtroNome"
            :disabled="loading"
            class="table-v-action-button"
            clearable
            dense
            hide-details
            placeholder="Nome do projeto"
            outlined
            style="max-width: 220px"
          ></v-text-field>
        </div>

        <div class="filtro mr-2">
          <label class="filtro-label" for="filtro-beneficio">Benefício</label>
          <v-select
            id="filtro-beneficio"
            v-model="filtroBeneficio"
            :items="beneficios"
            class="table-v-action-button"
            dense
            hide-details
            outlined
            style="max-width: 150px"
          ></v-select>
        </div>

        <div class="filtro">
          <label class="filtro-label" for="filtro-situacao">Ativo intangível</label>
          <v-select
            id="filtro-situacao"
            v-model="filtroSituacao"
            :items="situacoes"
            class="table-v-action-button"
            dense
            hide-details
            outlined
            style="max-width: 190px"
          ></v-select>
        </div>
      </v-card-text>

      <v-data-table
        id="table_v"
        :class="{ 'tabela-coluna-fixa-sombra': sombraAcoes }"
        :headers="headers"
        :items="rows"
        :loading="loading"
        loading-text="Buscando dados..."
        no-data-text="Não existem dados para mostrar."
        height="calc(100vh - 362px)"
        item-key="id"
        item-class="table-v-tr"
        show-select
        :value="selecionados"
        @input="aoSelecionar"
        multi-sort
        hide-default-header
        hide-default-footer
        :options="opcoes"
        :server-items-length="totalRegistros"
        :mobile-breakpoint="0"
        @update:options="onChangePage"
      >

      <template
        v-slot:header="{ props: { headers, options }, on: { sort, 'toggle-select-all': toggleSelectAll } }"
      >
        <thead class="v-data-table-header">
          <tr>
            <th
              v-for="head in headers"
              :key="head.value"
              :class="[
                `text-${head.align || 'start'}`,
                {
                  sortable: head.sortable !== false,
                  active: colunaOrdenada(head, options),
                  asc: !colunaDescendente(head, options),
                  desc: colunaDescendente(head, options),
                  nowrap: head.nowrapHeader !== false
                }
              ]"
              :data-label="head.text"
              :width="head.value === 'data-table-select' ? '48px' : head.width"
              :style="{ minWidth: head.width, maxWidth: head.width }"
              role="columnheader"
              scope="col"
            >
              <!-- Seleção de todas as linhas -->
              <v-simple-checkbox
                v-if="head.value === 'data-table-select'"
                class="mt-0"
                :value="todosSelecionados"
                :indeterminate="algunsSelecionados"
                :ripple="false"
                @click.stop="toggleSelectAll(!paginaTodaSelecionada)"
              ></v-simple-checkbox>

              <template v-else>
                <div class="th-conteudo" :style="estiloCabecalho(head)">
                  <span class="th-texto" @click="head.sortable !== false && sort(head.value)">
                    {{ head.text }}
                  </span>
                  <i
                    v-if="head.sortable !== false"
                    aria-hidden="true"
                    class="v-icon notranslate v-data-table-header__icon mdi mdi-chevron-down theme--light"
                    style="font-size: 18px"
                    @click="sort(head.value)"
                  ></i>
                </div>
              </template>
            </th>
          </tr>
        </thead>
      </template>

      <!-- Projeto -->
      <template #[`item.nome`]="{ item }">
        <span
          class="clamp-duas-linhas"
          @mouseenter="mostrarTooltip($event, item.nome)"
          @mouseleave="fecharTooltip"
        >
          {{ item.nome }}
        </span>
      </template>

      <!-- Tipo de pesquisa -->
      <template #[`item.tipo_pesquisa`]="{ item }">
        <span
          class="clamp-duas-linhas texto-justificado"
          @mouseenter="mostrarTooltip($event, textoPesquisa(item))"
          @mouseleave="fecharTooltip"
        >
          {{ textoPesquisa(item) }}
        </span>
      </template>

      <!-- Benefício -->
      <template #[`item.trabalho`]="{ item }">
        <div class="d-flex align-center" :class="{ 'justify-center': item.beneficios.length !== 2 }">
          <v-tooltip
            v-for="beneficio in item.beneficios"
            :key="`ben-${item.id}-${beneficio.trabalho}`"
            bottom
            :content-class="beneficio.trabalho === 2 ? 'tooltip-beneficio-li' : 'tooltip-beneficio-lb'"
          >
            <template v-slot:activator="{ on }">
              <v-chip
                v-on="on"
                small
                label
                :color="corBeneficio(beneficio.trabalho)"
                :class="classeBeneficio(beneficio.trabalho)"
                class="mr-1 font-weight-bold"
              >
                {{ beneficio.label }}
              </v-chip>
            </template>
            {{ nomeBeneficio(beneficio.trabalho) }}
          </v-tooltip>

          <v-tooltip v-if="!item.beneficios.length" bottom content-class="tooltip-escuro">
            <template v-slot:activator="{ on }">
              <v-chip
                v-on="on"
                small
                label
                color="grey lighten-3"
                class="grey--text text--darken-2 font-weight-bold"
              >
                ???
              </v-chip>
            </template>
            Indefinido
          </v-tooltip>
        </div>
      </template>

      <template #[`item.enquadramento`]="{ item }">
        <div class="d-flex justify-center">
          <v-tooltip v-if="!item.enquadramento" bottom content-class="tooltip-escuro">
            <template v-slot:activator="{ on }">
              <v-chip
                v-on="on"
                small
                label
                color="grey lighten-3"
                class="grey--text text--darken-2 font-weight-bold"
              >
                ???
              </v-chip>
            </template>
            Indefinido
          </v-tooltip>

          <v-chip
            v-else
            small
            label
            :color="corSituacao(item.enquadramento)"
            :class="classeSituacao(item.enquadramento)"
            class="font-weight-bold"
          >
            {{ item.enquadramento }}
          </v-chip>
        </div>
      </template>

      <!-- Marcação do ativo intangível -->
      <template #[`item.ativo_intangivel`]="{ item }">
        <div class="d-flex justify-center">
          <v-chip
            small
            label
            :color="corSituacao(item.ativo_intangivel)"
            :class="classeSituacao(item.ativo_intangivel)"
            class="font-weight-bold"
          >
            {{ item.ativo_intangivel || "Não avaliado" }}
          </v-chip>
        </div>
      </template>

      <!-- Horas em timesheet -->
      <template #[`item.horas_timesheet`]="{ item }">
        {{ item.horas_timesheet | toCurrencyValue }}
      </template>

      <template #[`item.dispendio`]="{ item }">
        <div class="d-flex flex-column align-end">
          <span v-for="dispendio in item.dispendios" :key="`disp-${item.id}-${dispendio.trabalho}`">
            {{ dispendio.valor | toCurrency }}
          </span>
        </div>
      </template>

      <!-- Ações -->
      <template #[`item.acoes`]="{ item }">
        <div class="d-flex align-center">
          <v-tooltip bottom content-class="tooltip-escuro">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon small :aria-label="`marcar-sim-${item.id}`" @click.stop="marcar(item, 'Sim')">
                <v-icon small color="green darken-3">
                  mdi-check
                </v-icon>
              </v-btn>
            </template>
            Marcar projeto como Ativo intangível
          </v-tooltip>

          <v-tooltip bottom content-class="tooltip-escuro">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon small :aria-label="`marcar-nao-${item.id}`" @click.stop="marcar(item, 'Não')">
                <v-icon small color="red darken-3">
                  mdi-close
                </v-icon>
              </v-btn>
            </template>
            Marcar projeto como não Ativo
          </v-tooltip>

          <v-tooltip bottom content-class="tooltip-escuro">
            <template v-slot:activator="{ on }">
              <v-badge dot :value="temObservacao(item)" color="primary" overlap offset-x="10" offset-y="10">
                <v-btn icon small v-on="on" :aria-label="`observacao-${item.id}`" @click.stop="abrirObservacao(item)">
                  <v-icon small :color="temObservacao(item) ? 'primary' : 'grey darken-1'">
                    mdi-comment-text-outline
                  </v-icon>
                </v-btn>
              </v-badge>
            </template>
            <span v-if="temObservacao(item)">
              <u><strong>Observação:</strong></u><br />
              {{ item.observacao }}
            </span>
            <span v-else>Sem observações</span>
          </v-tooltip>
        </div>
      </template>

      </v-data-table>

      <div class="rodape-tabela d-flex align-center flex-wrap">
        <div class="d-flex align-center px-2">
          <v-chip
            small
            label
            :color="corSituacao('Sim')"
            :class="classeSituacao('Sim')"
            class="mr-3 font-weight-bold"
          >
            Sim · {{ totais.sim }}
          </v-chip>

          <v-chip
            small
            label
            :color="corSituacao('Não')"
            :class="classeSituacao('Não')"
            class="mr-3 font-weight-bold"
          >
            Não · {{ totais.nao }}
          </v-chip>

          <v-chip
            small
            label
            :color="corSituacao('')"
            :class="classeSituacao('')"
            class="font-weight-bold"
          >
            Não avaliado · {{ totais.nao_avaliado }}
          </v-chip>
        </div>

        <v-data-footer
          class="ml-auto"
          :options="opcoes"
          :pagination="paginacao"
          :items-per-page-options="itemsPerPageOptions"
          items-per-page-text="Registros por página"
          @update:options="onFooterOptions"
        >
          <template v-slot:page-text="pagina">
            {{ pagina.pageStart }} - {{ pagina.pageStop }} de {{ pagina.itemsLength }}
          </template>
        </v-data-footer>
      </div>
    </v-card>

    <!-- Tooltip única da grade (só para texto cortado) — reposiciona ao trocar de âncora. -->
    <g-tooltip ref="tooltip" placement="bottom" :max-width="600" />

    <!-- Observação do projeto -->
    <v-dialog v-model="dialogObservacao" max-width="560" persistent>
      <v-card>
        <v-card-title class="pb-1">Observação do projeto</v-card-title>
        <v-card-text>
          <div class="text-subtitle-2 mb-3">{{ projetoEmEdicao ? projetoEmEdicao.nome : "" }}</div>
          <v-textarea
            v-model="observacao"
            outlined
            rows="4"
            label="Observação (por que foi ou não considerada ativo intangível)"
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="px-6 pb-3 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="secondary" class="px-5" dark text @click="dialogObservacao = false">Cancelar</v-btn>
          <v-btn color="primary" dark depressed class="px-5 ml-3" @click="salvarObservacao">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.faixa-selecao {
  border-radius: 4px;
  min-height: 44px;
}

::v-deep .table-v-action-button .v-input__slot {
  background-color: #ffffff;
}

/* Cabeçalho do card: 8px nas verticais e 16px nas laterais. */
.barra-acoes {
  padding: 8px 16px;
}

.filtro {
  display: flex;
  flex-direction: column;
}

.filtro-label {
  margin: 0 0 2px 10px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  color: #3e3e3f;
}

/* Botões sem label alinhados ao meio do campo (mesmo deslocamento do label). */
.alinhar-campo {
  margin-top: 15px;
}

.clamp-duas-linhas {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
}

.texto-justificado {
  text-align: justify;
}

/* Layout automático com piso por coluna: a coluna cresce se o conteúdo precisar. */
::v-deep .v-data-table__wrapper table {
  table-layout: auto;
  /* Abaixo deste piso as colunas se espremem e a tabela rola na horizontal. */
  min-width: 1200px;
}

/* Valores e horas não quebram: a coluna cresce. Vale só para o corpo. */
::v-deep td.text-end,
::v-deep td .text-end {
  white-space: nowrap;
}

/* Pílulas com o rótulo em uma linha (o Vuetify aplica `max-width: 100%`). */
::v-deep .v-chip {
  max-width: none;
}

::v-deep .v-chip .v-chip__content {
  white-space: nowrap;
}

::v-deep thead.v-data-table-header > tr {
  border-bottom: 0 !important;
  position: sticky;
  top: 0;
  z-index: 2;
}

/* Coluna de Ações fixa: o fundo opaco evita que o conteúdo rolado apareça por baixo. */
::v-deep thead.v-data-table-header th:last-child,
::v-deep tbody tr:not(.v-data-table__empty-wrapper) > td:last-child {
  position: sticky;
  right: 0;
  background-color: #ffffff;
}

::v-deep thead.v-data-table-header th:last-child {
  z-index: 3;
}

::v-deep tbody tr:not(.v-data-table__empty-wrapper) > td:last-child {
  z-index: 1;
}

/* Sombra da coluna fixa: pseudo-elemento com gradiente (`box-shadow` não aparece). */
::v-deep .tabela-coluna-fixa-sombra thead.v-data-table-header th:last-child::after,
::v-deep .tabela-coluna-fixa-sombra tbody tr:not(.v-data-table__empty-wrapper) > td:last-child::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 8px;
  transform: translateX(-100%);
  background-image: linear-gradient(to left, rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0));
  pointer-events: none;
}

/* Linha em destaque: a célula fixa herda a cor da linha. */
::v-deep tbody tr:hover:not(.v-data-table__empty-wrapper) > td:last-child,
::v-deep tbody tr.v-data-table__selected > td:last-child {
  background-color: inherit;
}

/* Cabeçalho: texto e ícone são itens flex (ícone ao lado, sem quebrar linha). */
.th-conteudo {
  display: flex;
  align-items: center;
  width: 100%;
}

.th-texto {
  min-width: 0;
}

.nowrap .th-texto {
  flex: 0 0 auto;
}

/* Ícone de ordenação sempre visível (o Vuetify só mostra no hover/coluna ativa).
   No hover ele herda a cor do cabeçalho, igual à coluna em uso. */
.th-conteudo .v-data-table-header__icon {
  flex: 0 0 auto;
  opacity: 1;
}

th.sortable:hover .th-conteudo .v-data-table-header__icon,
th.active .th-conteudo .v-data-table-header__icon {
  color: inherit;
}

.rodape-tabela {
  background-color: #ffffff;
}

/* Remove o arredondado inferior esquerdo para o branco unir ao rodapé. */
::v-deep .v-data-table {
  border-radius: 0 !important;
}
</style>

<!-- O tooltip é movido para `[data-app]` (mixin `detachable`): este bloco não pode ser `scoped`. -->
<style>
.tooltip-escuro {
  background-color: rgba(33, 33, 33, 0.97) !important;
  text-align: justify;
  white-space: normal;
  overflow-wrap: break-word;
  width: auto;
  padding: 8px 14px;
  font-size: 13px;
  line-height: 1.45;
}

.tooltip-beneficio-lb {
  background-color: #bbdefb !important; /* blue lighten-4 */
  color: #1565c0 !important; /* blue darken-3 */
  font-weight: 700;
}

.tooltip-beneficio-li {
  background-color: #d1c4e9 !important; /* deep-purple lighten-4 */
  color: #5e35b1 !important; /* deep-purple darken-3 */
  font-weight: 700;
}
</style>
